import React, { useEffect, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

let isRefreshing = false;
let failedRequestQueue: Array<any> = [];

function getAPIURL() {
  const isQA =
    import.meta.env.DEV ||
    window.location.hostname === 'oqpwahom.web.app' ||
    window.location.hostname === 'oqpwahom.firebaseapp.com';

  return isQA ? 'https://api.qa.oqdigital.com' : 'https://api3.oqdigital.com';
}

const api = axios.create({
  baseURL: getAPIURL(),
});

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { signOut, client } = useAuth();
  const clientRef = useRef(client);
  clientRef.current = client;
  const navigate = useNavigate();
  useEffect(() => {
    api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError<any, any>) => {
        if (error.response?.status === 401) {
          const originalConfig: any = error.config;
          const { refresh }: any = error.response.config.headers;

          if (!isRefreshing) {
            isRefreshing = true;
            const parameters = {
              refresh_token: refresh,
              searchFunctionality: 'refreshToken',
            };

            api
              .post('/search', parameters)
              .then((response) => {
                const { accessToken, refreshToken } = response.data.data;
                api.defaults.headers.common['Authorization'] =
                  `OQVCQUER ${accessToken}`;
                api.defaults.headers.common['refresh'] = refreshToken;

                failedRequestQueue.forEach((request) =>
                  request.onSuccess(accessToken, refreshToken)
                );
                failedRequestQueue = [];
              })
              .catch((error) => {
                failedRequestQueue.forEach((request) =>
                  request.onFailure(error)
                );
                failedRequestQueue = [];
              })
              .finally(() => {
                isRefreshing = false;
              });
          }

          return new Promise((resolve, reject) => {
            failedRequestQueue.push({
              onSuccess: (token: string, refresh: string) => {
                originalConfig.headers['Authorization'] = `OQVCQUER ${token}`;
                originalConfig.headers['refresh'] = refresh;
                resolve(api(originalConfig));
              },
              onFailure: (error: AxiosError) => {
                signOut();
                const clientUrl = clientRef.current?.clientUrl;
                if (clientUrl) {
                  navigate(`/_/${clientUrl}`);
                }
                reject(error);
              },
            });
          });
        }
        throw error;
      }
    );
  }, []);

  return <>{children}</>;
};

export { api, getAPIURL };
